"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import type { IndicadoresMunicipio, ValoresMunicipio } from "@/types/models/indicadores";
import { indicadoresMunicipioService } from "@/lib/services/indicadores.service";
import { groupValoresByIndicador, ValoresByIndicador } from "@/lib/mappers/indicadores.mappers";

export type ValorFormRow = {
  id?: number;
  ts: string;
  valor: number;
  indicador?: number;
  _deleted?: boolean;
};

export type IndicadorSubmitPayload = {
  indicator: Partial<IndicadoresMunicipio>;
  valores: ValorFormRow[];
};

export function useMunicipioIndicadoresData() {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const t = useTranslations("indicadoresView.toasts");

  const [indicadores, setIndicadores] = useState<IndicadoresMunicipio[]>([]);
  const [valoresByIndicador, setValoresByIndicador] = useState<ValoresByIndicador>({});
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const tenantId = currentTenantId ? Number(currentTenantId) : null;
  const canFetch = isLoggedIn && !!tenantId;

  async function refetchIndicadores() {
    if (!canFetch || !tenantId) return [];

    const data = await indicadoresMunicipioService.fetchIndicadoresByTenant({
      tenantId,
      userLang,
    });

    setIndicadores(data);
    return data;
  }

  async function refetchValores() {
    if (!canFetch || !tenantId) return [];

    const data = await indicadoresMunicipioService.fetchValoresByTenant({
      tenantId,
    });

    setValoresByIndicador(groupValoresByIndicador(data));
    return data;
  }

  async function fetchValoresByIndicador(indicadorId: number): Promise<ValoresMunicipio[]> {
    const data = await indicadoresMunicipioService.fetchValoresByIndicador({
      indicadorId,
    });

    setValoresByIndicador((prev: any) => ({
      ...prev,
      [indicadorId]: data,
    }));

    return data;
  }

  async function refetchAll() {
    if (!canFetch || !tenantId) return;

    setIsFetching(true);
    setError(null);

    try {
      await Promise.all([refetchIndicadores(), refetchValores()]);
    } catch (err) {
      setError(err);
      toast.error(t("loadError"), {
        description: t("tryAgain"),
      });
    } finally {
      setIsFetching(false);
    }
  }

  async function saveIndicador({ indicator, valores }: IndicadorSubmitPayload) {
    setIsSaving(true);

    try {
      let indicadorId = indicator.id;

      const indicatorPayload = {
        nombre: indicator.nombre,
        descripcion: indicator.descripcion,
        unidad: indicator.unidad,
        positivo: indicator.positivo,
        categoria: indicator.categoria,
        fuente: indicator.fuente,
        tenant: indicator.tenant,
        translations: indicator.translations ?? [],
      };

      if (indicadorId) {
        await indicadoresMunicipioService.updateIndicador(Number(indicadorId), indicatorPayload);
      } else {
        const created = await indicadoresMunicipioService.createIndicador(indicatorPayload);
        indicadorId = created.id;
      }

      if (!indicadorId) throw new Error("No se pudo obtener el id del indicador");

      const toCreate = valores
        .filter((v) => !v.id && !v._deleted)
        .map((v) => ({ ts: String(v.ts).trim(), valor: Number(v.valor), indicador: Number(indicadorId) }))
        .filter((v) => !Number.isNaN(v.valor));

      const toUpdate = valores
        .filter((v) => v.id && !v._deleted)
        .map((v) => ({ id: Number(v.id), ts: String(v.ts).trim(), valor: Number(v.valor), indicador: Number(indicadorId) }))
        .filter((v) => !Number.isNaN(v.valor));

      const toDelete = valores.filter((v) => v.id && v._deleted);

      if (toCreate.length) await indicadoresMunicipioService.createValores(toCreate);

      for (const v of toUpdate) {
        await indicadoresMunicipioService.updateValor(v.id, {
          ts: v.ts,
          valor: v.valor,
          indicador: v.indicador,
        });
      }

      for (const v of toDelete) {
        await indicadoresMunicipioService.deleteValor(Number(v.id));
      }

      await refetchAll();

      toast.success(indicator.id ? t("updateSuccess") : t("createSuccess"));
    } catch (err) {
      toast.error(t("saveError"));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteIndicador(id: number) {
    try {
      await indicadoresMunicipioService.deleteIndicador(id);
      await refetchAll();
      toast.success(t("deleteSuccess"));
    } catch (err) {
      toast.error(t("deleteError"));
      throw err;
    }
  }

  useEffect(() => {
    if (!canFetch || !tenantId) {
      setIndicadores([]);
      setValoresByIndicador({});
      setError(null);
      return;
    }

    void refetchAll();
  }, [isLoggedIn, currentTenantId, userLang]);

  return {
    indicadores,
    valoresByIndicador,
    isFetching,
    isSaving,
    error,
    refetchAll,
    refetchIndicadores,
    refetchValores,
    fetchValoresByIndicador,
    saveIndicador,
    deleteIndicador,
  };
}
